import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../../components/ui/ToastProvider'
import { markNotificationRead } from '../api/notificationsApi'
import { NOTIFICATIONS_KEY } from './useNotifications'
import { UNREAD_COUNT_KEY } from './useUnreadCount'
import type { Notification, NotificationTargetType } from '../types'

/**
 * Hedef türü → rota eşlemesinin **tek kaynağı** (spec FR-020, contracts §6). Zil paneli ve liste
 * sayfası aynı eşlemeyi kullanır; kopyalanmaz.
 *
 * Prensip kararının ayrı bir detay sayfası yoktur; liste ekranına `?id=` ile gidilir ve ilgili kayıt
 * açılır — projede zaten kullanılan kalıp (`/tickets/detail?id=`).
 */
function routeFor(targetType: NotificationTargetType, targetId: number | null): string | null {
  if (targetId == null) return null
  switch (targetType) {
    case 'ACTION':
      return `/minutes/actions/${targetId}`
    case 'PRINCIPLE_DECISION':
      return `/minutes/principle-decisions?id=${targetId}`
    case 'MEETING':
      return `/minutes/meeting-minutes/meetings/${targetId}`
    default:
      return null
  }
}

/**
 * "Detaya git" akışı (spec FR-017 .. FR-023) — zil paneli ve liste sayfası ortak kullanır.
 *
 * <p>Tetikleyici artık satırın kendisi değil, satırdaki **"Detaya git" düğmesidir**: satır aynı
 * zamanda okundu anahtarını barındırdığından, tüm satırın tıklanabilir olması kazara yönlendirme
 * üretiyordu. Akışın geri kalanı değişmedi.
 *
 * Tek bir tıklamada üç iş yapılır:
 *
 * 1. **Okundu işaretle** — zaten okunmuşsa istek gönderilmez (gereksiz çağrı olmaz; sunucu da
 *    idempotenttir, bu yalnız ağ tasarrufudur).
 * 2. **Sayacı ve listeyi tazele** — mutasyon başarılı olunca her iki anahtar invalidate edilir, böylece
 *    rozet ve açık liste kullanıcı ek işlem yapmadan güncellenir (spec FR-019).
 * 3. **Yönlendir** — hedefe göre.
 *
 * **Hata dayanıklılığı (spec FR-022):** Okundu işaretleme isteği başarısız olsa bile **yönlendirme
 * yine yapılır** — kullanıcı bu hata yüzünden işinden alıkonmaz. Hata bildirimi merkezî hata
 * yönetimine (019) bırakılır; sayaç bir sonraki tazelemede doğru değerine döner.
 *
 * **`UNRESOLVED`** (bağ var ama hedef bulunamıyor): bildirim yine okundu işaretlenir, yönlendirme
 * yapılmaz ve kullanıcıya bilgi mesajı gösterilir (spec FR-021).
 *
 * **`NONE`** (hiç bağ yok): yalnız okundu işaretlenir; mesaj da üretilmez (spec FR-020.5).
 */
export function useNotificationNavigate(onNavigate?: () => void) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const qc = useQueryClient()
  const toast = useToast()

  const markRead = useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] })
      qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    },
  })

  return useCallback(
    (item: Notification) => {
      if (!item.read) {
        // Yönlendirmeyi BEKLETMEZ (FR-022): işaretleme başarısız olsa da kullanıcı hedefe gider.
        markRead.mutate(item.id)
      }

      if (item.targetType === 'UNRESOLVED') {
        toast.show({
          severity: 'info',
          summary: t('notifications.title'),
          detail: t('notifications.targetUnavailable'),
          life: 4000,
        })
        return
      }

      const route = routeFor(item.targetType, item.targetId)
      if (!route) return

      onNavigate?.()
      navigate(route)
    },
    [markRead, navigate, onNavigate, t, toast],
  )
}

/**
 * Satırın gidilebilir bir hedefi var mı? `NONE` satırında "Detaya git" düğmesi **hiç çizilmez**
 * (spec FR-020.5) — kullanıcıya tıklayınca hiçbir şey yapmayan bir düğme gösterilmez.
 */
export function isClickable(targetType: NotificationTargetType): boolean {
  return targetType !== 'NONE'
}
